// Short-lived spark bursts for planet collisions / near-passes.
// Cheap CPU-driven particles on a Points mesh; recycles a fixed pool.

import {
  AdditiveBlending,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Points,
  ShaderMaterial,
} from "three";

const POOL_SIZE = 120;
const LIFETIME = 0.55; // seconds

export interface Sparks {
  points: Points;
  burst(x: number, y: number, z: number, color?: Color, count?: number): void;
  update(t: number): void;
  dispose(): void;
}

export function createSparks(): Sparks {
  const positions = new Float32Array(POOL_SIZE * 3);
  const velocities = new Float32Array(POOL_SIZE * 3);
  const colors = new Float32Array(POOL_SIZE * 3);
  const born = new Float32Array(POOL_SIZE);
  const alive = new Uint8Array(POOL_SIZE);
  const sizes = new Float32Array(POOL_SIZE);

  const geom = new BufferGeometry();
  geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geom.setAttribute("color", new Float32BufferAttribute(colors, 3));
  geom.setAttribute("size", new Float32BufferAttribute(sizes, 1));
  const posAttr = geom.getAttribute("position") as Float32BufferAttribute;
  const colAttr = geom.getAttribute("color") as Float32BufferAttribute;
  const sizeAttr = geom.getAttribute("size") as Float32BufferAttribute;

  const mat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    vertexColors: true,
    uniforms: {},
    vertexShader: /* glsl */ `
      attribute float size;
      varying vec3 vColor;
      void main() {
        vColor = color;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * (300.0 / -mv.z);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vColor;
      void main() {
        vec2 d = gl_PointCoord - vec2(0.5);
        float r = length(d);
        float a = smoothstep(0.5, 0.0, r);
        if (a < 0.02) discard;
        gl_FragColor = vec4(vColor, a);
      }
    `,
  });

  const points = new Points(geom, mat);
  points.frustumCulled = false;

  let lastT = 0;
  let cursor = 0;

  function allocate(): number {
    // Linear probe; overwrite oldest if pool is full.
    for (let tries = 0; tries < POOL_SIZE; tries++) {
      const i = cursor;
      cursor = (cursor + 1) % POOL_SIZE;
      if (!alive[i]) return i;
    }
    const i = cursor;
    cursor = (cursor + 1) % POOL_SIZE;
    return i;
  }

  function burst(x: number, y: number, z: number, color?: Color, count = 18) {
    const c = color ?? new Color("#ffb766");
    for (let k = 0; k < count; k++) {
      const i = allocate();
      alive[i] = 1;
      born[i] = lastT;
      const ang = Math.random() * Math.PI * 2;
      const up = 2 + Math.random() * 3;
      const spd = 3 + Math.random() * 5;
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      velocities[i * 3] = Math.cos(ang) * spd;
      velocities[i * 3 + 1] = up;
      velocities[i * 3 + 2] = Math.sin(ang) * spd;
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
      sizes[i] = 2.2 + Math.random() * 2;
    }
  }

  function update(t: number) {
    const dt = Math.min(0.05, t - lastT);
    lastT = t;
    for (let i = 0; i < POOL_SIZE; i++) {
      if (!alive[i]) {
        // Hide this particle by moving it far away (no alpha on unused indices).
        sizeAttr.setX(i, 0);
        continue;
      }
      const age = t - born[i]!;
      if (age > LIFETIME) {
        alive[i] = 0;
        sizeAttr.setX(i, 0);
        continue;
      }
      // Euler integrate.
      velocities[i * 3 + 1]! -= 9 * dt; // gentle gravity
      positions[i * 3]! += velocities[i * 3]! * dt;
      positions[i * 3 + 1]! += velocities[i * 3 + 1]! * dt;
      positions[i * 3 + 2]! += velocities[i * 3 + 2]! * dt;
      posAttr.setXYZ(
        i,
        positions[i * 3]!,
        positions[i * 3 + 1]!,
        positions[i * 3 + 2]!,
      );
      // Fade color over age.
      const u = 1 - age / LIFETIME;
      colAttr.setXYZ(
        i,
        colors[i * 3]! * u,
        colors[i * 3 + 1]! * u,
        colors[i * 3 + 2]! * u,
      );
      sizeAttr.setX(i, sizes[i]! * (0.5 + 0.5 * u));
    }
    posAttr.needsUpdate = true;
    colAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
  }

  return {
    points,
    burst,
    update,
    dispose() {
      geom.dispose();
      mat.dispose();
    },
  };
}
